import { useEffect, useState } from "react";

import { DateRange } from "react-day-picker";
import { useParams } from "react-router-dom";

import {
  addDays,
  endOfWeek,
  format,
  formatISO,
  isValid,
  parseISO,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";
import { es } from "date-fns/locale";
import {
  Calendar,
  CheckCircle,
  Filter,
  Loader2,
  Mail,
  Phone,
  PieChart,
  TrendingUp,
  UserCircle,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Button } from "~/components/ui/button";
import { Calendar as CalendarComponent } from "~/components/ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { supabase } from "~/integrations/supabase/client";
import { fetchSellerDetails } from "~/services/sellerService";
import { SalesRequest } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";

interface SellerProfile {
  id: string;
  name: string;
  lastname: string;
  email: string;
  phone: string | null;
  created_at: string;
}

interface SellerClient {
  id: string;
  name: string;
  email: string | null;
  tax_id: string | null;
  insured_amount: number | null;
  trailing_payment_days: number | null;
}

// For the charts
interface SalesData {
  date: string;
  formattedDate: string;
  sales: number;
  orders: number;
}

type TimePeriod = "daily" | "weekly" | "monthly";

interface StatusDistribution {
  name: string;
  value: number;
  color: string;
}

interface PaymentTypeDistribution {
  name: string;
  value: number;
  color: string;
}

const colorPalette = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

const SellerDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [seller, setSeller] = useState<SellerProfile | null>(null);
  const [salesRequests, setSalesRequests] = useState<SalesRequest[]>([]);
  const [filteredSalesRequests, setFilteredSalesRequests] = useState<
    SalesRequest[]
  >([]);
  const [clients, setClients] = useState<SellerClient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Chart data
  const [salesData, setSalesData] = useState<SalesData[]>([]);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>("daily");
  const [statusDistribution, setStatusDistribution] = useState<
    StatusDistribution[]
  >([]);
  const [paymentTypeDistribution, setPaymentTypeDistribution] = useState<
    PaymentTypeDistribution[]
  >([]);

  // Filter states
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subMonths(new Date(), 6),
    to: new Date(),
  });
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<string>("all");

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setIsLoading(true);

        // Fetch seller profile
        const { data: sellerData, error: sellerError } = await supabase
          .from("profiles")
          .select("id, name, lastname, email, phone, created_at")
          .eq("id", id)
          .single();

        if (sellerError) {
          console.error("Error fetching seller:", sellerError);
          setError("No se pudo cargar la información del vendedor");
          return;
        }

        setSeller(sellerData);

        // Fetch seller's sales requests and clients
        const { salesRequests, clients } = await fetchSellerDetails(id);
        setSalesRequests(salesRequests);
        setFilteredSalesRequests(salesRequests);
        setClients(clients);

        // Process data for charts
        processChartData(salesRequests);
      } catch (err) {
        console.error("Failed to fetch seller details:", err);
        setError("Ocurrió un error inesperado al cargar los datos");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Process the sales requests data for charts
  const processChartData = (
    requests: SalesRequest[],
    period: TimePeriod = timePeriod
  ) => {
    if (!requests.length) return;

    // Process sales data based on time period
    const salesByDate: Map<string, { sales: number; orders: number }> =
      new Map();

    // Sort requests by date
    const sortedRequests = [...requests].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    // Create date buckets based on selected time period
    if (sortedRequests.length > 0) {
      // Find min and max dates for creating the date range
      const minDate = new Date(sortedRequests[0].createdAt);
      const maxDate = new Date(
        sortedRequests[sortedRequests.length - 1].createdAt
      );

      // Ensure maxDate is at least today if max from data is in the past
      const today = new Date();
      const effectiveMaxDate = maxDate < today ? today : maxDate;

      // Generate all dates in the range
      let currentDate = new Date(minDate);
      while (currentDate <= effectiveMaxDate) {
        const dateKey = formatISO(currentDate, { representation: "date" });

        // Initialize with zero values
        salesByDate.set(dateKey, { sales: 0, orders: 0 });

        // Increment by one day
        currentDate = addDays(currentDate, 1);
      }
    }

    // Aggregate request data into the appropriate buckets
    requests.forEach((request) => {
      if (!request.createdAt) return;

      const requestDate = new Date(request.createdAt);
      if (!isValid(requestDate)) return;

      const dateKey = formatISO(requestDate, { representation: "date" });

      // Add to daily data (we need this for all periods)
      const dailyData = salesByDate.get(dateKey) || { sales: 0, orders: 0 };
      dailyData.sales += request.totalAmount;
      dailyData.orders += 1;
      salesByDate.set(dateKey, dailyData);
    });

    // Convert to array and format based on selected period
    let formattedData: SalesData[] = [];

    if (period === "daily") {
      // For daily view, use the data as is
      formattedData = Array.from(salesByDate.entries()).map(
        ([dateStr, data]) => {
          const date = parseISO(dateStr);
          return {
            date: dateStr,
            formattedDate: format(date, "dd MMM", { locale: es }),
            sales: data.sales,
            orders: data.orders,
          };
        }
      );
    } else if (period === "weekly") {
      // For weekly view, group by week
      const weeklyData: Map<
        string,
        { sales: number; orders: number; startDate: Date }
      > = new Map();

      Array.from(salesByDate.entries()).forEach(([dateStr, data]) => {
        const date = parseISO(dateStr);
        const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // Week starts on Monday
        const weekKey = formatISO(weekStart, { representation: "date" });

        if (!weeklyData.has(weekKey)) {
          weeklyData.set(weekKey, {
            sales: 0,
            orders: 0,
            startDate: weekStart,
          });
        }

        const weekData = weeklyData.get(weekKey)!;
        weekData.sales += data.sales;
        weekData.orders += data.orders;
      });

      formattedData = Array.from(weeklyData.entries()).map(([_, data]) => {
        const weekEnd = endOfWeek(data.startDate, { weekStartsOn: 1 });
        return {
          date: formatISO(data.startDate, { representation: "date" }),
          formattedDate: `${format(data.startDate, "dd MMM", { locale: es })} - ${format(weekEnd, "dd MMM", { locale: es })}`,
          sales: data.sales,
          orders: data.orders,
        };
      });
    } else if (period === "monthly") {
      // For monthly view, group by month
      const monthlyData: Map<string, { sales: number; orders: number }> =
        new Map();

      Array.from(salesByDate.entries()).forEach(([dateStr, data]) => {
        const date = parseISO(dateStr);
        const monthKey = format(date, "yyyy-MM");

        if (!monthlyData.has(monthKey)) {
          monthlyData.set(monthKey, { sales: 0, orders: 0 });
        }

        const monthData = monthlyData.get(monthKey)!;
        monthData.sales += data.sales;
        monthData.orders += data.orders;
      });

      formattedData = Array.from(monthlyData.entries()).map(
        ([monthKey, data]) => {
          const [year, month] = monthKey.split("-");
          const date = new Date(parseInt(year), parseInt(month) - 1, 1);
          return {
            date: monthKey,
            formattedDate: format(date, "MMM yyyy", { locale: es }),
            sales: data.sales,
            orders: data.orders,
          };
        }
      );
    }

    // Sort by date
    formattedData.sort((a, b) => a.date.localeCompare(b.date));

    setSalesData(formattedData);

    // Process status distribution using the dedicated function
    processStatusDistribution(requests);

    // Process payment type distribution
    const paymentTypeCounts: Record<string, number> = {
      prepaid: 0,
      future: 0,
    };

    requests.forEach((request) => {
      const paymentType = request.paymentType || "unknown";
      paymentTypeCounts[paymentType] =
        (paymentTypeCounts[paymentType] || 0) + 1;
    });

    const paymentTypeData = [
      {
        name: "Prepago",
        value: paymentTypeCounts.prepaid || 0,
        color: "#0088FE",
      },
      {
        name: "Crédito",
        value: paymentTypeCounts.future || 0,
        color: "#8884D8",
      },
    ];

    // Add any other payment types that might be in the data
    Object.entries(paymentTypeCounts)
      .filter(
        ([type]) =>
          type !== "prepaid" && type !== "future" && type !== "unknown"
      )
      .forEach(([type, count], index) => {
        paymentTypeData.push({
          name: type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, " "),
          value: count,
          color: colorPalette[index % colorPalette.length],
        });
      });

    setPaymentTypeDistribution(paymentTypeData);
  };

  // Process status distribution
  const processStatusDistribution = (requests: SalesRequest[]) => {
    // Dynamically collect all unique statuses present in the data
    const statusCounts: Record<string, number> = {};

    requests.forEach((request) => {
      const status = request.status || "unknown";
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    // Define a color palette for statuses
    const statusColors: Record<string, string> = {
      accepted: "#00C49F",
      pending: "#FFBB28",
      rejected: "#FF8042",
      cancelled: "#9370DB",
      pending_approval: "#4682B4",
      approved: "#00C49F", // Same as accepted for consistency
      unknown: "#A9A9A9",
    };

    // Transform to the format needed for the pie chart
    const statusData = Object.entries(statusCounts).map(([status, count]) => ({
      name: getStatusDisplayName(status),
      value: count,
      color:
        statusColors[status] ||
        "#" + Math.floor(Math.random() * 16777215).toString(16), // Random color for unknown statuses
    }));

    setStatusDistribution(statusData);
  };

  // Function to get display name for a status
  const getStatusDisplayName = (status: string): string => {
    switch (status) {
      case "accepted":
        return "Aprobadas";
      case "approved":
        return "Aprobadas";
      case "pending":
        return "Pendientes";
      case "pending_approval":
        return "Pendientes de Aprobación";
      case "rejected":
        return "Rechazadas";
      case "cancelled":
        return "Canceladas";
      case "in_transit":
        return "En Camino";
      case "delivered":
        return "Entregado";
      case "unknown":
        return "Desconocido";
      default:
        return (
          status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")
        );
    }
  };

  // Apply filters to sales requests
  useEffect(() => {
    if (!salesRequests.length) return;

    let filtered = [...salesRequests];

    // Apply date filter
    if (dateRange?.from && dateRange?.to) {
      filtered = filtered.filter((request) => {
        const requestDate = new Date(request.createdAt);
        return requestDate >= dateRange.from! && requestDate <= dateRange.to!;
      });
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((request) => request.status === statusFilter);
    }

    // Apply payment type filter
    if (paymentTypeFilter !== "all") {
      filtered = filtered.filter(
        (request) => request.paymentType === paymentTypeFilter
      );
    }

    setFilteredSalesRequests(filtered);

    // Update charts with filtered data
    processChartData(filtered);
  }, [dateRange, statusFilter, paymentTypeFilter, salesRequests]);

  // Handle time period change
  const handleTimePeriodChange = (newPeriod: TimePeriod) => {
    setTimePeriod(newPeriod);
    processChartData(filteredSalesRequests, newPeriod);
  };

  // Handle date range preset selections
  const handleDatePreset = (
    preset:
      | "last7days"
      | "last30days"
      | "last3months"
      | "last6months"
      | "last12months"
  ) => {
    const today = new Date();
    let from;

    switch (preset) {
      case "last7days":
        from = subDays(today, 7);
        break;
      case "last30days":
        from = subDays(today, 30);
        break;
      case "last3months":
        from = subMonths(today, 3);
        break;
      case "last6months":
        from = subMonths(today, 6);
        break;
      case "last12months":
        from = subMonths(today, 12);
        break;
    }

    setDateRange({ from, to: today });
  };

  // Get CSS classes for a status badge
  const getStatusBadgeClasses = (status: string): string => {
    switch (status) {
      case "accepted":
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-purple-100 text-purple-800";
      case "pending":
      case "pending_approval":
        return "bg-yellow-100 text-yellow-800";
      case "in_transit":
        return "bg-blue-100 text-blue-800";
      case "delivered":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Function to check if a sales request is accepted in any possible status form
  const isAcceptedOrApproved = (status: string): boolean => {
    return (
      status === "accepted" ||
      status === "approved" ||
      status === "in_transit" ||
      status === "delivered"
    );
  };

  // Function to get the total of accepted/approved sales requests
  const getAcceptedSalesTotal = (requests: SalesRequest[]): number => {
    return requests
      .filter((req) => isAcceptedOrApproved(req.status))
      .reduce((sum, req) => sum + req.totalAmount, 0);
  };

  // Function to get count of accepted/approved sales requests
  const getAcceptedSalesCount = (requests: SalesRequest[]): number => {
    return requests.filter((req) => isAcceptedOrApproved(req.status)).length;
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !seller) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-destructive">
          {error || "No se encontró el vendedor"}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[100vw] space-y-6 px-6 md:max-w-[60vw] lg:max-w-[70vw] lg:px-10 xl:max-w-[80vw]">
      {/* Seller Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle>Perfil del Vendedor</CardTitle>
          <CardDescription>
            Información de contacto y datos del vendedor
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <UserCircle className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">
                {seller.name} {seller.lastname}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <span>{seller.email}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Phone className="h-5 w-5 text-muted-foreground" />
              <span>{seller.phone || "No disponible"}</span>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium">Fecha de registro</p>
              <p>
                {new Date(seller.created_at).toLocaleDateString("es-ES", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium">Total de clientes</p>
              <p>{clients.length}</p>
            </div>
            <div>
              <p className="text-sm font-medium">Total de notas de venta</p>
              <p>{salesRequests.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter Controls - Moved to top of page */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-2">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            <CardTitle className="text-base">Filtros</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setDateRange({
                from: subMonths(new Date(), 6),
                to: new Date(),
              });
              setStatusFilter("all");
              setPaymentTypeFilter("all");
            }}
          >
            Restablecer
          </Button>
        </CardHeader>
        <CardContent className="py-2">
          <div className="flex flex-row flex-wrap items-end gap-3">
            {/* Date Range Filter */}
            <div className="space-y-1">
              <p className="text-xs font-medium">Rango de fechas</p>
              <div className="flex items-center gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 text-xs"
                    >
                      <Calendar className="mr-2 h-3 w-3" />
                      {dateRange?.from ? (
                        dateRange.to ? (
                          <>
                            {format(dateRange.from, "dd/MM/yy")} -{" "}
                            {format(dateRange.to, "dd/MM/yy")}
                          </>
                        ) : (
                          format(dateRange.from, "dd/MM/yy")
                        )
                      ) : (
                        <span>Seleccionar</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      initialFocus
                      mode="range"
                      defaultMonth={dateRange?.from}
                      selected={dateRange}
                      onSelect={setDateRange}
                      numberOfMonths={2}
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Quick Date Presets */}
            <div className="flex flex-wrap gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => handleDatePreset("last7days")}
              >
                7d
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => handleDatePreset("last30days")}
              >
                30d
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => handleDatePreset("last3months")}
              >
                3m
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => handleDatePreset("last6months")}
              >
                6m
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => handleDatePreset("last12months")}
              >
                1a
              </Button>
            </div>

            {/* Status Filter */}
            <div className="ml-auto space-y-1">
              <p className="text-xs font-medium">Estado</p>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 w-[130px] text-xs">
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="accepted">Aprobadas</SelectItem>
                  <SelectItem value="rejected">Rechazadas</SelectItem>
                  <SelectItem value="in_transit">En Camino</SelectItem>
                  <SelectItem value="delivered">Entregado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Payment Type Filter */}
            <div className="space-y-1">
              <p className="text-xs font-medium">Tipo de pago</p>
              <Select
                value={paymentTypeFilter}
                onValueChange={setPaymentTypeFilter}
              >
                <SelectTrigger className="h-8 w-[130px] text-xs">
                  <SelectValue placeholder="Todos los tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  <SelectItem value="prepaid">Prepago</SelectItem>
                  <SelectItem value="future">Crédito</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance Dashboard */}
      <Card>
        <CardHeader>
          <CardTitle>Dashboard de Rendimiento</CardTitle>
          <CardDescription>
            Indicadores de rendimiento del vendedor
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* KPI Row */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Card className="bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Ventas Totales
                    </p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(
                        filteredSalesRequests.reduce(
                          (sum, req) => sum + req.totalAmount,
                          0
                        )
                      )}
                    </p>
                  </div>
                  <div className="rounded-full bg-blue-100 p-2">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Ventas Aprobadas
                    </p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(
                        getAcceptedSalesTotal(filteredSalesRequests)
                      )}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {getAcceptedSalesCount(filteredSalesRequests)} notas
                      aprobadas
                    </p>
                  </div>
                  <div className="rounded-full bg-green-100 p-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-amber-50">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Tasa de Aprobación
                    </p>
                    <p className="text-2xl font-bold">
                      {filteredSalesRequests.length
                        ? `${Math.round((getAcceptedSalesCount(filteredSalesRequests) / filteredSalesRequests.length) * 100)}%`
                        : "0%"}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Incluye aprobadas, en camino y entregadas
                    </p>
                  </div>
                  <div className="rounded-full bg-amber-100 p-2">
                    <PieChart className="h-5 w-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Sales Over Time Chart */}
            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-medium">
                  Ventas a lo largo del tiempo
                </h3>
                <Tabs
                  defaultValue="daily"
                  value={timePeriod}
                  onValueChange={(value) =>
                    handleTimePeriodChange(value as TimePeriod)
                  }
                  className="h-8"
                >
                  <TabsList className="h-8">
                    <TabsTrigger value="daily" className="h-6 px-2 text-xs">
                      Diario
                    </TabsTrigger>
                    <TabsTrigger value="weekly" className="h-6 px-2 text-xs">
                      Semanal
                    </TabsTrigger>
                    <TabsTrigger value="monthly" className="h-6 px-2 text-xs">
                      Mensual
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={salesData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="formattedDate"
                      tick={{ fontSize: 10 }}
                      interval={
                        timePeriod === "daily"
                          ? salesData.length > 30
                            ? 6
                            : 1
                          : 0
                      }
                    />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => formatCurrency(value as number)}
                      labelFormatter={(label) => `Fecha: ${label}`}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="sales"
                      name="Ventas"
                      stroke="#8884d8"
                      fill="#8884d8"
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              {salesData.length === 0 && (
                <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
                  No hay datos de ventas para el período seleccionado
                </div>
              )}
            </div>

            {/* Status Distribution - Now takes up full width of its cell */}
            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <h3 className="mb-2 text-base font-medium">Estado de Notas</h3>
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={statusDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={statusDistribution.length <= 3}
                      label={
                        statusDistribution.length <= 3
                          ? ({ name, percent }) =>
                              `${name}: ${(percent * 100).toFixed(0)}%`
                          : undefined
                      }
                      outerRadius={110}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${value} notas`, "Cantidad"]}
                      itemSorter={(item) => -(item.value as number)}
                    />
                    {statusDistribution.length > 3 && (
                      <Legend
                        layout="vertical"
                        align="right"
                        verticalAlign="middle"
                      />
                    )}
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              {statusDistribution.length === 0 && (
                <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
                  No hay datos para mostrar
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sales Requests Card */}
      <Card>
        <CardHeader>
          <CardTitle>Notas de Venta</CardTitle>
          <CardDescription>
            Historial de notas de venta generadas por este vendedor
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Estado Original</TableHead>
                <TableHead>Monto</TableHead>
                <TableHead>Fecha de pago</TableHead>
                <TableHead>Tipo de pago</TableHead>
                <TableHead>Fecha de creación</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSalesRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center">
                    No se encontraron notas de venta con los filtros aplicados
                  </TableCell>
                </TableRow>
              ) : (
                filteredSalesRequests.map((request, index) => (
                  <TableRow
                    key={request.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell className="font-medium">
                      {request.id.slice(0, 8)}...
                    </TableCell>
                    <TableCell>
                      <div
                        className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${getStatusBadgeClasses(
                          request.status
                        )}`}
                      >
                        {getStatusDisplayName(request.status)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div
                        className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${getStatusBadgeClasses(
                          request.status
                        )}`}
                      >
                        {request.status}
                      </div>
                    </TableCell>
                    <TableCell>{formatCurrency(request.totalAmount)}</TableCell>
                    <TableCell>
                      {request.paymentDueDate
                        ? new Date(request.paymentDueDate).toLocaleDateString(
                            "es-ES"
                          )
                        : "No definida"}
                    </TableCell>
                    <TableCell>
                      {request.paymentType === "prepaid"
                        ? "Prepago"
                        : request.paymentType === "future"
                          ? "Crédito"
                          : request.paymentType || "No definido"}
                    </TableCell>
                    <TableCell>
                      {new Date(request.createdAt).toLocaleDateString("es-ES")}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Clients Card */}
      <Card>
        <CardHeader>
          <CardTitle>Clientes Asignados</CardTitle>
          <CardDescription>Clientes asignados a este vendedor</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>ID Tributario</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Monto Asegurado</TableHead>
                <TableHead>Días de Pago Pendiente</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center">
                    Este vendedor no tiene clientes asignados
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client, index) => (
                  <TableRow
                    key={client.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell className="font-medium">{client.name}</TableCell>
                    <TableCell>{client.tax_id || "-"}</TableCell>
                    <TableCell>{client.email || "-"}</TableCell>
                    <TableCell>
                      {client.insured_amount
                        ? formatCurrency(client.insured_amount)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {client.trailing_payment_days
                        ? client.trailing_payment_days.toString()
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default SellerDetail;
